import type { Question } from '@tabel/shared/schemas/questions';
import { CircleAlert, CircleCheck, ListChecks, MessageCircleQuestion, Search, X } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { ApiError } from '../lib/errors';
import type { HoursFormat } from '../lib/format';
import {
  answerSummary,
  compareQuestions,
  dayLabel,
  factShort,
  isAnswered,
  isUnanswered,
  toReview,
  typeLabel,
  typeOrder,
  typeTone,
} from '../lib/questions';
import { errorText } from './ErrorToast';
import { StateBadges } from './QuestionBlock';
import { Badge, Button, Callout, Segmented, SidePanel, fieldCls } from './ui';

/*
 * «Вопросы по табелю» — режим правой панели (003, US2; FR-021, FR-024; contracts/ui-panel.md, «Режимы правой
 * панели»): фильтр «Без ответа / Отвечены / Все», группировка «По датам / По людям / По типу», поиск по ФИО, отбор по
 * дате, человеку, «давно без ответа» и «новые» (из шапки таблицы и полосы показателей). Бригадир отмечает вопросы
 * одного типа и отвечает сразу всем; без выбора — «Ответить по очереди». Разные типы — «Ответить всем» недоступна,
 * рядом объяснение. Пустые состояния объясняют, что делать.
 */

export type ListFilter = 'open' | 'done' | 'all';
export type ListQuick = 'old' | 'new' | 'review';
type Group = 'date' | 'person' | 'type';

/** С чем открыть список: фильтр и отбор. */
export interface ListPreset {
  filter?: ListFilter;
  quick?: ListQuick | null;
  date?: string | null;
  personId?: string | null;
}

const QUICK_LABEL: Record<ListQuick, string> = {
  old: 'давно без ответа',
  new: 'новые за вчера',
  review: 'ответы на проверку',
};

/** Чип отбора с ✕. */
function Chip({
  label,
  value,
  onClear,
  name,
}: { label: string; value: string; onClear: () => void; name: string }) {
  return (
    <span className="inline-flex h-7 max-w-full items-center gap-1 rounded-full border border-line bg-subtle pr-0.5 pl-2.5 text-[12.5px] text-ink-2 max-sm:h-10">
      <span className="min-w-0 truncate">
        <b className="font-medium text-ink">{label}:</b> {value}
      </span>
      <button
        type="button"
        aria-label={`Убрать отбор: ${name}`}
        title="Убрать отбор"
        onClick={onClear}
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-ink-3 hover:bg-surface hover:text-ink max-sm:h-9 max-sm:w-9"
      >
        <X size={13} strokeWidth={1.75} aria-hidden />
      </button>
    </span>
  );
}

/** Флажок выбора: цель нажатия не ниже 40 px. */
function Check({
  label,
  checked,
  onChange,
}: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="inline-flex min-h-10 min-w-10 shrink-0 cursor-pointer items-center justify-center">
      <input
        type="checkbox"
        aria-label={label}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 cursor-pointer accent-accent"
      />
    </label>
  );
}

function Empty({
  ok,
  title,
  text,
  action,
}: { ok: boolean; title: string; text: string; action?: ReactNode }) {
  const Icon = ok ? CircleCheck : Search;
  return (
    <div className="flex flex-col items-center px-4 py-10 text-center">
      <span
        className={`mb-3 inline-flex h-10 w-10 items-center justify-center rounded-card ${ok ? 'bg-ok-soft text-ok' : 'bg-accent-soft text-accent'}`}
      >
        <Icon size={20} strokeWidth={1.75} aria-hidden />
      </span>
      <p className="text-[15px] font-semibold text-ink">{title}</p>
      <p className="mt-1 max-w-sm text-[13px] text-ink-2">{text}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export interface QuestionsListProps {
  open: boolean;
  onClose: () => void;
  onBack?: () => void;
  timesheetName: string;
  questions: readonly Question[];
  loading?: boolean;
  error?: unknown;
  /** Бригадир отвечает: выбор вопросов, «Ответить всем», «Ответить по очереди». */
  canAnswer: boolean;
  /** Кадры: по умолчанию — ответы. */
  review?: boolean;
  /** С чем открыть. Новый отбор — новый ключ компонента на странице. */
  preset: ListPreset;
  tzOffset: string;
  format: HoursFormat | null;
  onOpen: (q: Question) => void;
  /** Ответить сразу на выбранные (одного типа), в порядке списка. */
  onBulk: (ids: number[]) => void;
  onQueue: () => void;
  returnFocus?: () => HTMLElement | null | undefined;
}

export function QuestionsList({
  open,
  onClose,
  onBack,
  timesheetName,
  questions,
  loading,
  error,
  canAnswer,
  review = false,
  preset,
  tzOffset,
  format,
  onOpen,
  onBulk,
  onQueue,
  returnFocus,
}: QuestionsListProps) {
  const [filter, setFilter] = useState<ListFilter>(preset.filter ?? (review ? 'done' : 'open'));
  const [group, setGroup] = useState<Group>('date');
  const [search, setSearch] = useState('');
  const [quick, setQuick] = useState<ListQuick | null>(preset.quick ?? null);
  const [date, setDate] = useState<string | null>(preset.date ?? null);
  const [personId, setPersonId] = useState<string | null>(preset.personId ?? null);
  const [sel, setSel] = useState<ReadonlySet<number>>(new Set());

  const selectable = (q: Question) => canAnswer && isUnanswered(q);
  const counts = {
    open: questions.filter(isUnanswered).length,
    done: questions.filter(isAnswered).length,
    all: questions.length,
  };
  const s = search.trim().toLowerCase();
  const items = questions
    .filter((q) => (filter === 'open' ? isUnanswered(q) : filter === 'done' ? isAnswered(q) : true))
    .filter((q) =>
      quick === 'old'
        ? q.old && isUnanswered(q)
        : quick === 'new'
          ? q.isNew
          : quick === 'review'
            ? toReview(q)
            : true,
    )
    .filter((q) => !date || q.date === date)
    .filter((q) => !personId || q.personId === personId)
    .filter((q) => !s || q.displayName.toLowerCase().includes(s))
    .sort(compareQuestions);
  const chosen = items.filter((q) => sel.has(q.id) && selectable(q));
  const sameType = new Set(chosen.map((q) => q.type)).size <= 1;
  const filtered = Boolean(date || personId || quick);

  const toggle = (ids: number[], on: boolean) =>
    setSel((x) => {
      const next = new Set(x);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  const resetFilters = () => {
    setDate(null);
    setPersonId(null);
    setQuick(null);
    setSearch('');
  };

  // Группы по датам, людям или типу: порядок — от старой даты, по ФИО, по типу.
  const groups = new Map<string, Question[]>();
  for (const q of items) {
    const k = group === 'date' ? q.date : group === 'person' ? q.displayName : q.type;
    groups.set(k, [...(groups.get(k) ?? []), q]);
  }
  const keys = [...groups.keys()];
  if (group === 'person') keys.sort((a, b) => a.localeCompare(b, 'ru'));
  if (group === 'type') keys.sort((a, b) => typeOrder(a) - typeOrder(b));
  const groupLabel = (k: string) => (group === 'date' ? dayLabel(k) : group === 'type' ? typeLabel(k) : k);

  const personName = personId
    ? (questions.find((q) => q.personId === personId)?.displayName ?? personId)
    : null;

  let body: ReactNode;
  if (loading) body = <p className="text-[13px] text-ink-3">Загрузка вопросов…</p>;
  else if (error) {
    const e = error instanceof ApiError ? error : null;
    const text = errorText(error, 'Не удалось загрузить');
    body = (
      <Callout tone="bad" icon={CircleAlert}>
        Вопросы не загрузились: {text}.{e?.requestId ? ` Код ошибки: ${e.requestId}.` : ''}
      </Callout>
    );
  } else if (questions.length === 0) {
    body = (
      <Empty
        ok
        title="Вопросов по табелю нет"
        text="Вопросы появляются, когда в законченном дне сработали правила: нет входа, нет выхода, больше 24 ч, потерян проход, две бригады."
      />
    );
  } else if (items.length === 0) {
    const allDone = !review && filter === 'open' && counts.open === 0;
    const okHere = !review && filter === 'open' && filtered && !s;
    body = (
      <Empty
        ok={allDone || okHere}
        title={
          allDone ? 'Все вопросы отвечены' : okHere ? 'Здесь вопросов без ответа нет' : 'Ничего не найдено'
        }
        text={allDone || okHere ? 'Ответы стоят в ячейках табеля.' : 'Измените фильтр или поиск.'}
        action={
          (filtered || s) && !allDone ? (
            <Button onClick={resetFilters}>Показать все вопросы</Button>
          ) : undefined
        }
      />
    );
  } else {
    body = (
      <div className="flex flex-col">
        {keys.map((k) => {
          const list = groups.get(k) ?? [];
          const pickable = list.filter(selectable);
          const allOn = pickable.length > 0 && pickable.every((q) => sel.has(q.id));
          return (
            <div key={k} className="flex flex-col gap-1.5 pb-3">
              <div
                data-group
                className="flex min-h-8 items-center gap-1 text-[12.5px] font-semibold text-ink"
              >
                {pickable.length > 0 && (
                  <Check
                    label={`Выбрать группу: ${groupLabel(k)}`}
                    checked={allOn}
                    onChange={(v) =>
                      toggle(
                        pickable.map((q) => q.id),
                        v,
                      )
                    }
                  />
                )}
                <span>{groupLabel(k)}</span>{' '}
                <span className="font-medium text-ink-3 tabular-nums">{list.length}</span>
              </div>
              {list.map((q) => {
                const on = sel.has(q.id);
                return (
                  <div
                    key={q.id}
                    data-question={q.id}
                    className={`flex items-start rounded-control border transition-colors ${on ? 'border-accent/40 bg-accent-soft' : 'border-line bg-surface hover:border-line-strong hover:bg-subtle'}`}
                  >
                    {selectable(q) ? (
                      <Check
                        label={`Выбрать: ${q.displayName}, ${dayLabel(q.date)}`}
                        checked={on}
                        onChange={(v) => toggle([q.id], v)}
                      />
                    ) : (
                      <span className="w-3 shrink-0" aria-hidden />
                    )}
                    <button
                      type="button"
                      onClick={() => onOpen(q)}
                      className="flex min-h-10 min-w-0 flex-1 flex-wrap items-start justify-between gap-x-2 gap-y-1 py-2.5 pr-3 text-left"
                    >
                      <span className="flex min-w-0 flex-col gap-1">
                        <span className="flex flex-wrap items-baseline gap-x-2 text-[13.5px]">
                          <b className="font-medium text-ink">{q.displayName}</b>
                          <span className="text-[12.5px] text-ink-3">{dayLabel(q.date)}</span>
                        </span>
                        <span className="flex flex-wrap items-center gap-2 text-[12.5px] text-ink-2">
                          <Badge tone={typeTone(q.type)}>{typeLabel(q.type)}</Badge>
                          <span>{factShort(q)}</span>
                        </span>
                        {q.answer && isAnswered(q) && (
                          <span className="text-[12.5px] text-ok">
                            {answerSummary(q.answer, format, tzOffset)}
                          </span>
                        )}
                        {q.state === 'later' && (
                          <span className="text-[12.5px] text-ink-2">
                            Уточню позже{q.answer?.note ? `: ${q.answer.note}` : ''}
                          </span>
                        )}
                        {q.state === 'clarify' && q.review?.comment && (
                          <span className="text-[12.5px] text-warn">Кадры: «{q.review.comment}»</span>
                        )}
                      </span>
                      <span className="flex flex-wrap items-center justify-end gap-1">
                        <StateBadges q={q} />
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }

  const filters: { value: ListFilter; label: string; count: number }[] = review
    ? [
        { value: 'done', label: 'Ответы', count: counts.done },
        { value: 'open', label: 'Без ответа', count: counts.open },
        { value: 'all', label: 'Все', count: counts.all },
      ]
    : [
        { value: 'open', label: 'Без ответа', count: counts.open },
        { value: 'done', label: 'Отвечены', count: counts.done },
        { value: 'all', label: 'Все', count: counts.all },
      ];

  let footer: ReactNode = null;
  if (chosen.length > 0) {
    footer = (
      <>
        <span className="text-[13px] font-medium text-ink tabular-nums">Выбрано {chosen.length}</span>
        <Button variant="ghost" onClick={() => setSel(new Set())}>
          Снять выбор
        </Button>
        <span className="flex-1" aria-hidden />
        {!sameType && (
          <span className="text-[12.5px] text-ink-3">Разные типы вопросов — отвечайте по одному</span>
        )}
        <Button
          variant="primary"
          icon={ListChecks}
          disabled={!sameType}
          onClick={() => onBulk(chosen.map((q) => q.id))}
        >
          Ответить всем
        </Button>
      </>
    );
  } else if (canAnswer && counts.open > 0) {
    footer = (
      <>
        <span className="text-[12.5px] text-ink-3 max-sm:hidden">
          Выберите вопросы одного типа, чтобы ответить сразу всем
        </span>
        <span className="flex-1" aria-hidden />
        <Button variant="primary" icon={MessageCircleQuestion} onClick={onQueue}>
          Ответить по очереди
        </Button>
      </>
    );
  }

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      onBack={onBack}
      label="Вопросы по табелю"
      crumb={timesheetName}
      title="Вопросы по табелю"
      icon={MessageCircleQuestion}
      footer={footer}
      returnFocus={returnFocus}
    >
      <div className="flex flex-col gap-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <Segmented<ListFilter>
            label="Фильтр"
            value={filter}
            onChange={setFilter}
            options={filters}
            className="max-sm:w-full"
          />
          <Segmented<Group>
            label="Группировка"
            value={group}
            onChange={setGroup}
            options={[
              { value: 'date', label: 'По датам' },
              { value: 'person', label: 'По людям' },
              { value: 'type', label: 'По типу' },
            ]}
            className="max-sm:w-full"
          />
        </div>
        <div className="relative">
          <Search
            size={16}
            strokeWidth={1.75}
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-3"
            aria-hidden
          />
          <input
            type="search"
            aria-label="Найти по ФИО"
            placeholder="Найти по ФИО"
            autoComplete="off"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`${fieldCls} w-full pl-9`}
          />
        </div>
        {filtered && (
          <div className="flex flex-wrap gap-1.5">
            {date && <Chip label="Дата" value={dayLabel(date)} name="дата" onClear={() => setDate(null)} />}
            {personName && (
              <Chip label="Сотрудник" value={personName} name="сотрудник" onClear={() => setPersonId(null)} />
            )}
            {quick && (
              <Chip
                label="Показаны"
                value={QUICK_LABEL[quick]}
                name="показаны"
                onClear={() => setQuick(null)}
              />
            )}
          </div>
        )}
      </div>
      {body}
    </SidePanel>
  );
}
